// Copyright (c) 2022 by Move Closer

import { isPlainObject } from 'lodash'
import { IConnector, Injectable, IEventbus, AnyObject } from '@movecloser/front-core'
import { SubscribeConfig } from '@kluseg/trackify'

import { logger } from '../../../../support'
import { ISiteService } from '../../../../contexts'

import { IUserComService, NewsletterFormData, UserComConfig } from './user-com.contracts'
import { LocaleContentRule } from '../locale-content-manager'

/**
 * @inheritDoc
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl> (edited)
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Katarzyna Otto <katarzyna.otto@movecloser.pl> (edited)
 */
@Injectable()
export class UserComService implements IUserComService {
  /**
   * Service's configuration.
   */
  private readonly config: UserComConfig

  /**
   * An instance of the service that implements the `IConnector` interface.
   */
  private readonly connector: IConnector
  private readonly siteService!: ISiteService

  private eventBus: IEventbus

  /**
   * Determines whether the User.com script has been successfully initialised.
   */
  private isInitialised: boolean = false

  private localeConfig?: LocaleContentRule

  constructor (config: UserComConfig, connector: IConnector, eventBus: IEventbus, siteService: ISiteService, localeConfig: LocaleContentRule) {
    if (typeof config === 'undefined') {
      throw new Error('UserComService.constructor(): Expected [config] argument to be defined!')
    }

    this.config = config

    if (typeof connector === 'undefined') {
      throw new Error('UserComService.constructor(): Expected [connector] argument to be defined!')
    }

    this.connector = connector
    this.eventBus = eventBus
    this.siteService = siteService
    this.localeConfig = localeConfig
  }

  /**
   * @inheritDoc
   */
  public subscribe (formData: NewsletterFormData | SubscribeConfig, eventName: string = 'subscribe'): void {
    const storeCode = this.siteService.getActiveSite().getProperty('storeCode', '')
    // @ts-expect-error - I can't make shims work :/
    const userengage = window.userengage
    // @ts-expect-error - I can't make shims work :/
    const UE = window.UE

    UE.pageHit({
      email: formData.email,
      user_id: new Date().getTime()
    })
    userengage(`event.${eventName}`, {
      ...formData,
      storeCode
    })
  }

  //
  // /**
  //  * @inheritDoc
  //  */
  // public async subscribe (formData: NewsletterFormData): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     if (!UserComService.isConfigValid(this.config)) {
  //       reject(new Error(('front.shared.newsletter.errorMessage')))
  //     }
  //
  //     const storeCode = this.siteService.getActiveSite().getProperty('storeCode', '')
  //
  //     // @ts-expect-error - I can't make shims work :/
  //     const userengage = window.userengage
  //     userengage('event.subscribe', {
  //       ...formData,
  //       storeCode
  //     })
  //     return 'subscribed'
  //   })
  // }

  /**
   * @inheritDoc
   */
  public async event (eventName: string, data: AnyObject): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window === 'undefined' || !window.userengage) {
      return
    }

    return new Promise((resolve, reject) => {
      if (!UserComService.isConfigValid(this.config)) {
        reject(new Error(('front.shared.newsletter.errorMessage')))
      }

      const storeCode = this.siteService.getActiveSite().getProperty('storeCode', '')
      // @ts-expect-error - I can't make shims work :/
      const userengage = window.userengage

      userengage(`event.${eventName}`, {
        ...data,
        storeCode
      })
    })
  }

  /**
   * @inheritDoc
   */
  public async updateUser (data: AnyObject): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window === 'undefined' || !window.userengage) {
      return
    }

    return new Promise((resolve, reject) => {
      if (!UserComService.isConfigValid(this.config)) {
        reject(new Error(('front.shared.newsletter.errorMessage')))
      }

      const storeCode = this.siteService.getActiveSite().getProperty('storeCode', '')
      // @ts-expect-error - I can't make shims work :/
      const userengage = window.userengage

      userengage('client.update', {
        ...data,
        storeCode
      })
    })
  }

  /**
   * Initializes the User.com JS script.
   */
  public async init (): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    if (!UserComService.isConfigValid(this.config)) {
      this.eventBus.emit('app:usercom.initialized', false)
      return
    }

    return new Promise((resolve, reject) => {
      let el: HTMLScriptElement | null =
        document.querySelector<HTMLScriptElement>(`script[src="${this.config.endpoint}"]`)

      if (el === null) {
        el = document.createElement('script')
        el.setAttribute('data-cfasync', 'false')
        el.src = this.config.endpoint
        el.type = 'text/javascript'
      }

      if (el.hasAttribute('data-loaded')) {
        resolve(Boolean(el.getAttribute('data-loaded')))
        return
      }

      el.addEventListener('error', reject)
      el.addEventListener('abort', reject)
      el.addEventListener('load', () => {
        if (el === null) {
          return reject(new Error('[el] is [null]!'))
        }

        el.setAttribute('data-loaded', 'true')
        resolve(true)
      })

      document.body.appendChild(el)
    }).then(() => {
      const localeCode = this.siteService.getActiveSiteLocale()
      if (!!this.localeConfig && !!this.localeConfig[localeCode] && this.localeConfig[localeCode].usercomChat) {
        /**
         * Piece of executable string that should be evaluated once the main
         * script from `loadScript()` method is ready.
         *
         * @see https://user.com/en/integrations/custom-script/
         */
        const _executable: string = `window.civchat = { apiKey: "${this.config.apiKey}" }`
        const userScript: HTMLScriptElement = document.createElement('script')
        userScript.setAttribute('data-cfasync', 'false')
        userScript.type = 'text/javascript'
        userScript.innerHTML = _executable

        document.body.appendChild(userScript)
      }

      this.isInitialised = true
      this.eventBus.emit('app:usercom.initialized', true)
    }).catch(error => {
      logger(error, 'error')
      this.eventBus.emit('app:usercom.initialized', false)
    })
  }

  /**
   * Validates the passed-in config object.
   *
   * @param config - The config object to validate.
   *
   * @throws Error - if the config is not valid.
   */
  private static isConfigValid (config: UserComConfig): boolean {
    if (!isPlainObject(config)) {
      logger(
        `UserComService.validateConfig(): Expected the [config] object to be a plain object, but got [${config}]!`,
        'warn'
      )
      return false
    }

    if (typeof config.apiKey !== 'string') {
      logger(
        `UserComService.validateConfig(): Expected the [config.apiKey] to be a type of [string], but got [${typeof config.apiKey}]!`,
        'warn'
      )
      return false
    }

    if (config.apiKey.length === 0) {
      logger('UserComService.validateConfig(): [config.apiKey] can NOT be empty!', 'warn')
      return false
    }

    if (typeof config.endpoint !== 'string') {
      logger(
        `UserComService.validateConfig(): Expected the [config.endpoint] to be a type of [string], but got [${typeof config.endpoint}]!`,
        'warn'
      )
      return false
    }

    if (config.endpoint.length === 0) {
      logger('UserComService.validateConfig(): [config.endpoint] can NOT be empty!', 'warn')
      return false
    }

    /**
     * Determines whether the `config.endpoint` is a valid URL address.
     */
    const isEndpointUrl: boolean = new RegExp(
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/g
    ).test(config.endpoint)

    if (!isEndpointUrl) {
      logger('UserComService.validateConfig(): Expected the [config.endpoint] to be a valid URL address', 'warn')
      return false
    }

    return true
  }

  /**
   * @inheritDoc
   */
  public initialized (): boolean {
    return this.isInitialised
  }
}
